import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  Card,
  ConfirmModal,
  Disclosure,
  Icon,
  Label,
  Modal,
  Page,
  RelativeDateTime,
  SimpleSelect,
  SimpleSummary,
  Skeleton,
  TextInput,
} from 'corso-admin-ui';
import { imageTypes } from 'corso-types';
import { FormEventHandler, ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DescriptionList, { Description } from '~/components/DescriptionList';
import EmptyStateAction from '~/components/EmptyStateAction';
import MediaUploader from '~/components/field/MediaUploader';
import { MediaThumbnail } from '~/components/media/MediaThumbnail';
import {
  useCustomFields,
  useCustomFieldsDelete,
  useCustomFieldsUpsert,
} from '~/features/settings/hooks/useCustomFields';
import {
  CustomField,
  CustomFieldCreate,
  customFieldCreateSchema,
  CustomFieldUpdate,
  customFieldUpdateSchema,
  UpsertFormProps,
} from '~/types';

function CustomFieldForm({
  show,
  onClose,
  onSubmit,
  values,
}: UpsertFormProps<CustomFieldCreate, CustomFieldUpdate, CustomField>) {
  const update = !!values?.id;

  const valueTypes = customFieldCreateSchema.shape.valueType.options.map(
    (option) => option.value,
  );

  const valueTypeOptions = valueTypes.map((valueType) => ({
    value: valueType,
    displayName: valueType,
  }));

  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    reset,
    register,
    formState,
  } = useForm({
    resolver: zodResolver(customFieldUpdateSchema.or(customFieldCreateSchema)),
    values,
  });

  const selectedValueType = watch('valueType');
  const referenceImages = watch('referenceImages') ?? [];

  const isSelectOrMultiSelect =
    selectedValueType === 'Select' || selectedValueType === 'MultiSelect';

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => {
      onSubmit(formValues);
      reset();
    })(event).catch(console.error);
  };

  return (
    <Modal
      title="Custom Fields"
      description="Custom fields allow you to collect and store additional information about a claim or registration."
      size="lg"
      show={show}
      onClose={onClose}
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="display-name"
          label="Name"
          details="This name will be used as a label on the form field in both the Customer App and Merchant Admin. (eg. 'Serial Number')"
          required
          {...register('displayName')}
          error={errors.displayName?.message}
        />

        <TextInput
          id="name"
          label="Description"
          details="This description will be used as a hint when filling out the field in both the Customer App and Merchant Admin. (eg. 'The Serial Number can be found on the back of the device')"
          required
          {...register('description')}
          error={errors.description?.message}
        />

        {!update ? (
          <Controller
            name="valueType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <SimpleSelect
                label="Value Type"
                options={valueTypeOptions.map((valueTypeOption) => ({
                  label: valueTypeOption.displayName,
                  value: valueTypeOption.value,
                }))}
                value={value}
                onChange={onChange}
                details="The type of value that will be stored in this field."
                error={
                  'valueType' in formState.errors &&
                  formState.errors.valueType?.message
                }
              />
            )}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Label>Value Type</Label>
            <div>
              <Badge>{values.valueType}</Badge>
            </div>
          </div>
        )}

        {isSelectOrMultiSelect && (
          <TextInput
            id="value-type-options"
            label="Options"
            details="A comma-separated list of options that the user can select from. (eg. 'Red, Blue, Green')"
            {...register('options')}
            error={errors.options?.message}
            required // required when valueType is 'Select'
          />
        )}

        <Controller
          name="referenceImages"
          control={control}
          render={({ field: { value = [], onChange } }) => (
            <MediaUploader
              id="reference-images"
              label="Reference Images"
              uploadPath="customFieldReferenceImage"
              accept={imageTypes}
              maxAssets={4}
              assets={value.map(({ url }, index) => ({
                name: `Image ${index + 1}`,
                src: url,
              }))}
              onChange={(assets) =>
                onChange(
                  assets.map(
                    ({ src }) =>
                      value.find((image) => image.url === src) ?? { url: src },
                  ),
                )
              }
            />
          )}
        />

        {referenceImages.map((image, index) => (
          <TextInput
            key={image.url}
            id={`reference-image-${index}-caption`}
            label={`Image ${index + 1} Caption`}
            details="Shown under the image when a customer enlarges it."
            maxLength={150}
            {...register(`referenceImages.${index}.caption`)}
            error={errors.referenceImages?.[index]?.caption?.message}
          />
        ))}

        <Action variant="primary" type="submit">
          {update ? 'Save' : 'Create'} Custom Field
        </Action>
      </form>
    </Modal>
  );
}

function CustomFieldsSkeleton() {
  return (
    <div className="flex gap-2">
      <Skeleton.Rectangle height="36px" width="100%" />
      <Skeleton.Rectangle height="36px" width="44px" />
      <Skeleton.Rectangle height="36px" width="44px" />
    </div>
  );
}

function DeleteCustomField({ cf }: { cf: CustomField }) {
  const { mutate: deleteCustomField, isPending } = useCustomFieldsDelete();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const confirmDelete = () => {
    deleteCustomField(cf);
    setShowConfirmation(false);
  };

  return (
    <>
      <Action
        icon="remove"
        variant="ghost"
        accessibilityLabel={`Delete ${cf.displayName}`}
        onClick={() => setShowConfirmation(true)}
      />

      <ConfirmModal
        title="Delete Custom Field"
        prompt={`Are you sure you want to delete ${cf.displayName}?`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showConfirmation}
        loading={isPending}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}

function EditCustomField({ cf }: { cf: CustomField }) {
  const { mutate: upsertCustomField } = useCustomFieldsUpsert();
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      <Action
        icon="edit"
        accessibilityLabel={`Edit ${cf.displayName} group`}
        onClick={() => setShowForm(true)}
        variant="ghost"
      />

      <CustomFieldForm
        show={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={(upsert) => {
          upsertCustomField(upsert);
          setShowForm(false);
        }}
        values={cf}
      />
    </>
  );
}

function CustomFieldDisplay({
  customField: {
    displayName,
    description,
    options,
    referenceImages,
    createdOn,
  },
  actions,
}: {
  customField: CustomField;
  actions?: ReactNode;
}) {
  const descriptions: Description[] = [];

  descriptions.push({
    term: 'Description',
    details: description,
  });

  if (options) {
    descriptions.push({
      term: 'Options',
      details: options,
    });
  }

  if (referenceImages?.length) {
    descriptions.push({
      term: 'Reference Images',
      details: (
        <ul className="flex gap-2">
          {referenceImages.map(({ url, caption }) => (
            <li key={url} className="size-16">
              <MediaThumbnail src={url} alt={caption ?? ''} />
            </li>
          ))}
        </ul>
      ),
    });
  }

  return (
    <Disclosure
      renderSummary={
        <SimpleSummary>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="flex flex-col items-start gap-1">
              <p className="truncate text-left text-sm font-medium text-balance text-neutral-700">
                {displayName}
              </p>

              {createdOn && (
                <p className="text-xs text-neutral-500">
                  Created <RelativeDateTime dateTime={createdOn} />
                </p>
              )}
            </div>

            {actions}
          </div>
        </SimpleSummary>
      }
    >
      <div className="mt-1 px-2">
        <DescriptionList descriptions={descriptions} />
      </div>
    </Disclosure>
  );
}

export default function CustomFieldSettingsPage() {
  const { data: customFields = null, isLoading } = useCustomFields();
  const [showForm, setShowForm] = useState(false);
  const { mutate: upsertCustomField } = useCustomFieldsUpsert();

  return (
    <Page
      title="Custom Fields"
      primaryAction={{
        onAction: () => setShowForm(true),
        icon: 'add',
        content: 'Add Custom Field',
      }}
    >
      <Card>
        <Skeleton
          instances={1}
          skeleton={CustomFieldsSkeleton}
          isLoading={isLoading}
        >
          {customFields?.length === 0 && !isLoading && (
            <EmptyStateAction.Button
              onClick={() => setShowForm(true)}
              icon={<Icon icon="customFields" />}
              label="Create a custom field"
              description="Custom fields allow you to allow you to collect and store additional information about a claim or registration."
            />
          )}
          {!!customFields?.length && (
            <ul className="flex flex-col gap-2">
              {customFields.map((cf) => (
                <li key={cf.id}>
                  <CustomFieldDisplay
                    customField={cf}
                    actions={
                      <div className="flex flex-row items-center gap-2">
                        <div>
                          <Badge>{cf.valueType}</Badge>
                        </div>
                        <EditCustomField cf={cf} />
                        <DeleteCustomField cf={cf} />
                      </div>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          <CustomFieldForm
            show={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={(upsert) => {
              upsertCustomField(upsert);
              setShowForm(false);
            }}
          />
        </Skeleton>
      </Card>
    </Page>
  );
}
